<template>
  <div>
    <v-expansion-panels
      v-if="!componentLoading"
      v-model="panel"
      multiple
      accordion
      active-class="p-0"
    >
      <v-expansion-panel
        v-for="(item,index) in items"
        :key="`expansion_tab_panel_${index}`"
      >
        <v-expansion-panel-header>
          {{ item.title }}
          <div v-if="item.showHourSorting && hourSort" class="col-2">
            <v-chip
              v-if="hourSort"
              class="ma-2"
              close
              @click:close="hourSort = null"
            >
              Trier par: {{ hourSort }}h
            </v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="mt-2">
            <v-row class="z3 mt-2" style="height: 10px;">
              <v-col cols="2" class="d-flex justify-center white-bg" />
            </v-row>
            <v-row>
              <v-col cols="2 py-0">
                <ContentTypeValueList class="px-1" :content-types="item.items" :horse-effort-stats="horseEffortStats" @orderHorseEffort="orderHorseEffort" />
              </v-col>
              <v-col cols="10" style="padding:12px 0;">
                <div :id="`contentTypePlanner-${tabKey}-${index}`" v-dragscroll class="full-height overflow-x-auto overflow-y-hidden all-scroll no-scroll" @dragscrollmove="syncScrollWith('mainPlanner', index)">
                  <HoursList v-if="item.showHourSorting" :id="`hourList-${tabKey}-${index}`" :show-sort="true" :hour-sort="hourSort" @sortHour="sortByHour" />
                  <HoursList v-else :id="`hourList-${tabKey}-${index}`" />
                  <div>
                    <ContentTimeTable v-if="item.items" :content-type-values="item.items" :content="tabKey" />
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <div v-else class="d-flex justify-center align-center full-height">
      <Loader />
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeMount } from '@vue/composition-api';
import store from '@/store';
import { EventBus, rowPerContentType, sortPlannerContentByHour, isSameMonth, sortArray, syncScrollWith } from '@/helpers/utils';
import { filterContentType } from '@/helpers/search';
import { separateContentTypesWithAndWithoutSession, computeEffort } from '@/helpers/dataManager';
import { horsesEffortSorting } from '@/configs/references';

import Loader from '@/components/common/Loader.vue';

import HoursList from '../HoursList.vue';
import ContentTimeTable from './ContentTimeTable.vue';
import ContentTypeValueList from './ContentTypeValueList.vue';

export default {
  components: { HoursList, ContentTimeTable, ContentTypeValueList, Loader },
  props: {
    tabKey: {
      type: String,
      default: ''
    }
  },
  setup (props) {
    const panel = ref([0, 1]);
    const crmUrl = `${process.env.VUE_APP_CRM_URL}?fromPegase=true`;
    const isCustomerTab = computed(() => props.tabKey === 'customer');
    const isHorseTab = computed(() => props.tabKey === 'horse');
    const selectedDate = computed(() => store.state.selectedDate);

    const hourSort = ref(null);

    const componentLoading = ref(false);

    const contentTypeValues = computed(() => {
      const filteredTypes = filterContentType(props.tabKey);

      if (isHorseTab.value) {
        return store.getters['horsesFilters/byPresentHorses'](filteredTypes);
      }

      return filteredTypes;
    });

    const changeFoldedPanel = () => {
      if (isCustomerTab.value) {
        panel.value = [1];
      }
    };

    const horseEffortStats = ref([
      { name: 'J', key: 'dailyEffort', value: 0, background: 'light-pink-bg' },
      { name: 'S', key: 'weeklyEffort', value: 0, background: 'dark-pink-bg' },
      { name: 'M', key: 'monthlyEffort', value: 0, background: 'heavy-pink-bg' }
    ]);

    const resetSorting = orderBy => {
      horseEffortStats.value = horseEffortStats.value.map(item => {
        if (item.key !== orderBy) {
          return { ...item, value: horsesEffortSorting.NONE };
        }
        return item;
      });
    };

    const orderHorseEffort = orderBy => {
      resetSorting(orderBy);

      const index = horseEffortStats.value.findIndex(item => item.key === orderBy);

      if (index === -1) return;

      const sortingType = horseEffortStats.value[index].value + 1;

      horseEffortStats.value[index].value = sortingType > 2 ? 0 : sortingType;
    };

    EventBus.$on('changeFolderPanel', changeFoldedPanel);

    const items = computed(() => {
      const contentTypes = separateContentTypesWithAndWithoutSession(props.tabKey);

      const uuids = contentTypeValues.value.map(ct => ct.uuid);

      let filteredWithSession = contentTypes.with_session.filter(ct => contentTypeValues.value.map(i => i.uuid).includes(ct.uuid));
      const filteredCustomersWithoutSession = contentTypes.without_session.filter(ct => contentTypeValues.value.map(i => i.uuid).includes(ct.uuid));

      rowPerContentType(filteredWithSession);

      if (hourSort.value) {
        filteredWithSession = sortPlannerContentByHour(filteredWithSession, hourSort.value);
      }

      let filteredWithoutSession = contentTypes.without_session.filter(ct => uuids.includes(ct.uuid));

      if (isHorseTab.value) {
        filteredWithSession = filteredWithSession.map(horse => ({
          ...horse,
          dailyEffort: computeEffort(store.getters.dailySessionsByContentType(horse), horse),
          weeklyEffort: computeEffort(store.getters.weeklySessionsByContentType(horse), horse),
          monthlyEffort: computeEffort(store.getters.monthlySessionsByContentType(horse), horse)
        }));

        filteredWithoutSession = filteredWithoutSession.map(horse => ({
          ...horse,
          dailyEffort: 0.0,
          weeklyEffort: computeEffort(store.getters.weeklySessionsByContentType(horse), horse),
          monthlyEffort: computeEffort(store.getters.monthlySessionsByContentType(horse), horse)
        }));

        if (horseEffortStats.value && horseEffortStats.value.some(stat => stat.value)) {
          const orderBy = horseEffortStats.value.find(stat => stat.value);

          const orderValue = orderBy.value === 1 ? '>' : '<';

          filteredWithSession = sortArray(filteredWithSession, orderBy.key, orderValue, true);
          filteredWithoutSession = sortArray(filteredWithoutSession, orderBy.key, orderValue, true);
        }
      }

      return [
        { title: 'Affectés', items: filteredWithSession, showHourSorting: true },
        { title: 'Non affectés', items: isCustomerTab.value ? filteredCustomersWithoutSession : filteredWithoutSession, showHorseCategory: true }
      ];
    });

    const sortByHour = hour => {
      hourSort.value = hour;
    };

    watch(selectedDate, async (newVal, oldVal) => {
      const sameMonth = isSameMonth(oldVal);
      if (!sameMonth && isHorseTab.value) {
        componentLoading.value = true;
        await store.dispatch('allMonthlySessions', 'horses');
        componentLoading.value = false;
      }
    });

    onBeforeMount(async () => {
      if (isHorseTab.value) {
        componentLoading.value = true;
        await store.dispatch('allMonthlySessions', 'horses');
        componentLoading.value = false;
      }
    });

    return { hourSort, sortByHour, panel, items, isCustomerTab, crmUrl, componentLoading, orderHorseEffort, horseEffortStats, syncScrollWith };
  }
};
</script>
